import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, BarChart3, Bot, ShieldCheck, LineChart, Bitcoin, Wallet, Trophy, Box, Smartphone,
  Sparkles, Users, Lock, Cloud, Fingerprint, PlayCircle,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const PILLARS = [
  { Icon: Users, title: "Built by investors, for investors", text: "Designed by people who have watched the tape for decades — around how real portfolios are built, hedged and rebalanced." },
  { Icon: Sparkles, title: "Powered by the latest AI", text: "Woodrow uses breakthroughs in large language models that simply weren't possible a short time ago to read the market every few minutes." },
  { Icon: ShieldCheck, title: "Risk first, always", text: "Stop-losses, trailing stops, drawdown floors and automatic hedging are enforced in code — the AI can't talk its way around them." },
];

const FEATURES = [
  { id: "woodrow", Icon: Bot, eyebrow: "Woodrow AI Trader", title: "An autonomous portfolio manager — yours alone",
    text: "Every member gets Woodrow: an AI trader running a simulated $100,000 portfolio around the clock, using your strategy, your sectors and your risk appetite.",
    bullets: ["Builds a fully invested starting portfolio in one click", "Re-evaluates stocks and crypto on your schedule", "Surgical Reoptimize swaps out only clear laggards"], img: "woodrow.jpg" },
  { id: "risk", Icon: ShieldCheck, eyebrow: "Risk Guardrails", title: "Discipline you can dial in",
    text: "Strategy settings put you in control: risk tolerance, position sizing, cash reserve and hold time — with guardrails enforced on every trade.",
    bullets: ["Stop-loss, trailing stop & portfolio drawdown floor", "Down-market hedging with inverse ETFs and bonds", "Sector weights from 100% crypto to zero crypto"], img: "woodrow-risk.jpg" },
  { id: "portfolio", Icon: LineChart, eyebrow: "Portfolio", title: "The whole market at a glance",
    text: "Live quotes across 114+ of the largest US companies and hedges, a scrolling ticker, top movers and your combined value trend — all in one view.",
    bullets: ["Live data from Finnhub, Yahoo Finance & CoinGecko", "Any stock you buy is tracked automatically", "Combined My Picks + Woodrow value trend"], img: "portfolio.jpg" },
  { id: "crypto", Icon: Bitcoin, eyebrow: "Crypto", title: "Crypto, 24/7 — side by side with stocks",
    text: "Track the top coins with 7-day sparklines and see exactly what you and Woodrow hold, with live profit and loss.",
    bullets: ["Top 20 coins by market cap", "Your holdings panel with live P&L", "\"Held\" badges right in the market table"], img: "crypto.jpg" },
  { id: "mypicks", Icon: Wallet, eyebrow: "My Picks", title: "Your own picks, scored honestly",
    text: "Run your own portfolio next to Woodrow's. Go long or short, set limit orders and see realized and unrealized results without the spin.",
    bullets: ["Long & short positions with limit orders", "Realized vs. unrealized P&L and win rate", "Full trade history grouped by date or symbol"], img: "mypicks.jpg" },
  { id: "leaderboard", Icon: Trophy, eyebrow: "Leaderboard", title: "See who's really beating the market",
    text: "Every Woodrow is ranked against every other — and against the S&P 500, Nasdaq, Dow, Bitcoin and Ethereum — over the window you choose.",
    bullets: ["1D, 1W, 1M, 3M and All-time windows", "Top Trader banner with strategy and alpha", "Names only — no personal details shared"], img: "leaderboard.jpg" },
  { id: "visualization", Icon: Box, eyebrow: "3D Visualization", title: "Every trade, in three dimensions",
    text: "Spin, zoom and scrub through time to see where the wins and losses come from — by trader, by sector and by size.",
    bullets: ["Trade scatter, trader towers and sector cubes", "Time slider to replay the action", "Your trades highlighted in gold"], img: "visualization.jpg" },
];

const WHY = [
  { Icon: Bot, title: "An AI that works while you don't", text: "Woodrow never sleeps, never panics and never forgets the plan." },
  { Icon: BarChart3, title: "Benchmarked, not hyped", text: "Every result is measured against the S&P 500 so you know what's real." },
  { Icon: Lock, title: "Private by design", text: "Approval-only access, an audit log of every admin action, names-only leaderboards." },
  { Icon: Cloud, title: "Cloud-native", text: "Nothing to install. Always current. Backs itself up." },
];

const ROLES = [
  ["Super Admin", "Approves members, manages users, sends welcomes and reviews the audit log. Doesn't trade."],
  ["Member", "Gets a personal Woodrow, My Picks, the leaderboard, visualization and every market view."],
];

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.5 } };

const Eyebrow = ({ Icon, children }) => (
  <div className="flex items-center gap-2 mb-4">
    <span className="w-8 h-8 border border-bt-gold/40 bg-bt-gold/10 flex items-center justify-center"><Icon className="w-4 h-4 text-bt-gold" strokeWidth={1.5} /></span>
    <span className="font-mono text-xs uppercase tracking-[0.25em] text-bt-text-secondary">{children}</span>
  </div>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2 mt-6">
    {items.map(b => <li key={b} className="flex gap-3 text-sm sm:text-base text-bt-text-primary"><span className="mt-2 w-1.5 h-1.5 bg-bt-gold shrink-0" />{b}</li>)}
  </ul>
);

const BrowserFrame = ({ src, alt }) => (
  <div className="border border-bt-border-subtle bg-bt-surface shadow-2xl overflow-hidden transition-transform duration-500 hover:-translate-y-1">
    <div className="flex gap-1.5 px-3 py-2 bg-bt-surface-2 border-b border-bt-border-subtle">{[0, 1, 2].map(i => <span key={i} className="w-2.5 h-2.5 rounded-full bg-bt-text-tertiary/40" />)}</div>
    <img src={`/learn/${src}`} alt={alt} loading="lazy" className="w-full block" />
  </div>
);

const FeatureRow = ({ f, flip }) => (
  <motion.section {...fadeUp} id={f.id} className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center py-14 sm:py-20" data-testid={`learn-feature-${f.id}`}>
    <div className={flip ? "lg:order-2" : ""}>
      <Eyebrow Icon={f.Icon}>{f.eyebrow}</Eyebrow>
      <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-bt-text-primary leading-tight">{f.title}</h2>
      <p className="mt-4 text-base text-bt-text-secondary leading-relaxed">{f.text}</p>
      <Bullets items={f.bullets} />
    </div>
    <div className={flip ? "lg:order-1" : ""}><BrowserFrame src={f.img} alt={f.eyebrow} /></div>
  </motion.section>
);

const CTAButtons = ({ signedIn, testPrefix }) => (
  <div className="flex flex-wrap gap-3">
    {signedIn ? (
      <Link to="/" className="inline-flex items-center gap-2 px-6 h-12 bg-bt-gold text-bt-bg font-medium hover:opacity-90 transition-opacity" data-testid={`${testPrefix}-dashboard`}>Go to dashboard <ArrowRight className="w-4 h-4" /></Link>
    ) : (
      <>
        <Link to="/login?request=1" className="inline-flex items-center gap-2 px-6 h-12 bg-bt-gold text-bt-bg font-medium hover:opacity-90 transition-opacity" data-testid={`${testPrefix}-request`}>Request access <ArrowRight className="w-4 h-4" /></Link>
        <Link to="/login" className="inline-flex items-center gap-2 px-6 h-12 border border-bt-border-focus text-bt-text-primary hover:border-bt-gold hover:text-bt-gold transition-colors" data-testid={`${testPrefix}-signin`}>Sign in</Link>
      </>
    )}
  </div>
);

const TopBar = () => (
  <header className="sticky top-0 z-40 backdrop-blur-xl bg-bt-bg/80 border-b border-bt-border-subtle">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
      <Link to="/learn" className="flex items-center gap-2" data-testid="learn-logo">
        <span className="w-8 h-8 bg-bt-gold/15 border border-bt-gold/40 flex items-center justify-center"><BarChart3 className="w-4 h-4 text-bt-gold" /></span>
        <span className="font-heading text-xl font-semibold text-bt-text-primary">Rockafeller Jones</span>
      </Link>
      <Link to="/login" className="inline-flex items-center gap-2 px-4 h-9 bg-bt-gold text-bt-bg text-sm font-medium hover:opacity-90" data-testid="learn-header-signin">Sign in <ArrowRight className="w-4 h-4" /></Link>
    </div>
  </header>
);

const playVideo = (e) => {
  e.preventDefault();
  document.getElementById("video")?.scrollIntoView({ behavior: "smooth", block: "center" });
  setTimeout(() => document.getElementById("learn-video")?.play().catch(() => {}), 600);
};

const VideoSection = () => (
  <motion.section {...fadeUp} id="video" className="py-12 sm:py-16" data-testid="learn-video-section">
    <p className="font-mono text-xs uppercase tracking-[0.25em] text-bt-text-secondary mb-3">See it in action</p>
    <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-bt-text-primary mb-8">A one-minute tour of Rockafeller Jones</h2>
    <div className="border border-bt-border-subtle bg-bt-surface shadow-2xl overflow-hidden">
      <video id="learn-video" controls playsInline preload="metadata" poster="/learn/walkthrough-poster.jpg" className="w-full block bg-black" data-testid="learn-video">
        <source src="/learn/walkthrough.mp4?v=2" type="video/mp4" />
        <source src="/learn/walkthrough.webm?v=2" type="video/webm" />
      </video>
    </div>
  </motion.section>
);

const Hero = ({ signedIn }) => (
  <section className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center pt-16 sm:pt-24 pb-10" data-testid="learn-hero">
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-bt-gold mb-5">Market intelligence · AI trading</p>
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-bt-text-primary leading-[1.05]">Wall Street discipline.<br />AI speed.</h1>
      <p className="mt-5 text-lg text-bt-text-secondary">Your markets, your picks and your own AI trader — beautifully connected.</p>
      <p className="mt-4 text-base text-bt-text-tertiary max-w-xl">Rockafeller Jones brings live stock and crypto data, portfolio tracking, a public leaderboard and Woodrow — an autonomous AI portfolio manager — into one modern home.</p>
      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <CTAButtons signedIn={signedIn} testPrefix="learn-hero" />
        <a href="#video" onClick={playVideo} className="inline-flex items-center gap-2 text-sm text-bt-text-primary hover:text-bt-gold transition-colors" data-testid="learn-watch-video">
          <PlayCircle className="w-5 h-5 text-bt-gold" /> Watch video
        </a>
        <a href="#features" className="text-sm text-bt-text-secondary hover:text-bt-gold underline underline-offset-4" data-testid="learn-explore-features">Explore features</a>
      </div>
    </motion.div>
    <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}>
      <BrowserFrame src="woodrow.jpg" alt="Woodrow dashboard" />
    </motion.div>
  </section>
);

const Pillars = () => (
  <section className="grid md:grid-cols-3 gap-px bg-bt-border-subtle border border-bt-border-subtle my-10" data-testid="learn-pillars">
    {PILLARS.map((p, i) => (
      <motion.div key={p.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }} className="bg-bt-surface p-6 sm:p-8">
        <p.Icon className="w-6 h-6 text-bt-gold mb-4" strokeWidth={1.5} />
        <h3 className="font-heading text-xl font-semibold text-bt-text-primary">{p.title}</h3>
        <p className="mt-2 text-sm text-bt-text-secondary leading-relaxed">{p.text}</p>
      </motion.div>
    ))}
  </section>
);

const MobileSection = () => (
  <motion.section {...fadeUp} className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-center py-14 sm:py-20" data-testid="learn-feature-mobile">
    <div className="mx-auto w-[260px] border-[10px] border-bt-surface-2 rounded-[2.2rem] overflow-hidden shadow-2xl">
      <img src="/learn/mobile.jpg" alt="Rockafeller Jones on mobile" loading="lazy" className="w-full block" />
    </div>
    <div>
      <Eyebrow Icon={Smartphone}>Mobile</Eyebrow>
      <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-bt-text-primary">Your trading desk, in your pocket</h2>
      <Bullets items={["Add to Home Screen — no app store needed", "Opens full-screen like a native app on iPhone and Android", "Touch-first layouts stay quick and readable on any screen"]} />
      <div className="mt-6 flex items-center gap-2 text-sm text-bt-text-secondary"><Fingerprint className="w-4 h-4 text-bt-gold" /> Sign in with Face ID or fingerprint on supported devices.</div>
    </div>
  </motion.section>
);

const WhySection = () => (
  <motion.section {...fadeUp} className="py-14 sm:py-20" data-testid="learn-why">
    <p className="font-mono text-xs uppercase tracking-[0.25em] text-bt-text-secondary mb-3">Why investors choose us</p>
    <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-bt-text-primary mb-10">Less noise. Better decisions.</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {WHY.map(w => (
        <div key={w.title} className="bg-bt-surface border border-bt-border-subtle p-6 transition-colors hover:border-bt-gold/50">
          <w.Icon className="w-5 h-5 text-bt-gold mb-3" strokeWidth={1.5} />
          <h3 className="font-medium text-bt-text-primary">{w.title}</h3>
          <p className="mt-1 text-sm text-bt-text-secondary">{w.text}</p>
        </div>
      ))}
    </div>
  </motion.section>
);

const RolesSection = () => (
  <motion.section {...fadeUp} className="grid lg:grid-cols-2 gap-10 py-14 sm:py-20 border-t border-bt-border-subtle" data-testid="learn-roles">
    <div>
      <Eyebrow Icon={Lock}>Roles &amp; access</Eyebrow>
      <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-bt-text-primary">Invitation only, by design</h2>
      <p className="mt-4 text-bt-text-secondary">Every member is approved by a person. Request access, and you'll get a welcome email with your temporary password once you're in.</p>
    </div>
    <div className="space-y-3">
      {ROLES.map(([r, d]) => (
        <div key={r} className="bg-bt-surface border border-bt-border-subtle p-5">
          <p className="font-mono text-xs uppercase tracking-wider text-bt-gold">{r}</p>
          <p className="mt-1 text-sm text-bt-text-secondary">{d}</p>
        </div>
      ))}
    </div>
  </motion.section>
);

const FinalCTA = ({ signedIn }) => (
  <motion.section {...fadeUp} className="my-14 sm:my-20 bg-bt-surface border border-bt-gold/40 p-8 sm:p-14 flex flex-col lg:flex-row lg:items-center justify-between gap-8" data-testid="learn-final-cta">
    <div>
      <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-bt-text-primary">Ready to put Woodrow to work?</h2>
      <p className="mt-2 text-bt-text-secondary">Request access today — we'll email you as soon as you're approved.</p>
    </div>
    <CTAButtons signedIn={signedIn} testPrefix="learn-final" />
  </motion.section>
);

export default function LearnPage() {
  const { user } = useAuth();
  const signedIn = !!user;
  return (
    <div className="min-h-screen bg-bt-bg" data-testid="learn-page">
      {!signedIn && <TopBar />}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
        <Hero signedIn={signedIn} />
        <VideoSection />
        <Pillars />
        <div id="features">{FEATURES.map((f, i) => <FeatureRow key={f.id} f={f} flip={i % 2 === 1} />)}</div>
        <MobileSection />
        <WhySection />
        <RolesSection />
        <FinalCTA signedIn={signedIn} />
        <footer className="py-8 border-t border-bt-border-subtle text-xs font-mono text-bt-text-tertiary flex flex-wrap gap-3 justify-between">
          <span>© 2025/2026 · Rockafeller Jones. All rights reserved.</span>
          <span>Simulated trading for education only · <Link to="/legal" className="underline hover:text-bt-gold" data-testid="learn-legal-link">Not financial advice</Link></span>
        </footer>
      </div>
    </div>
  );
}
